import axios, { type AxiosError, type AxiosRequestConfig, type InternalAxiosRequestConfig } from 'axios';
import { API_BASE_URL } from './config';
import { useAuthStore, type LoginResponse } from '../auth/authStore';

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  // Required so the wheels_refresh HttpOnly cookie is sent on refresh /
  // logout calls. CORS in the Api allows credentials in Development.
  withCredentials: true,
});

apiClient.interceptors.request.use((config) => {
  const token = useAuthStore.getState().accessToken;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  const slug = useAuthStore.getState().tenantSlug;
  if (slug && !config.headers['X-Tenant-Slug']) {
    config.headers['X-Tenant-Slug'] = slug;
  }
  return config;
});

let refreshInflight: Promise<string | null> | null = null;

async function refreshAccessToken(tenantSlug: string | null): Promise<string | null> {
  if (refreshInflight) {
    return refreshInflight;
  }

  refreshInflight = (async () => {
    try {
      const headers: Record<string, string> = {};
      if (tenantSlug) {
        headers['X-Tenant-Slug'] = tenantSlug;
      }
      const response = await axios.post<LoginResponse>(
        `${API_BASE_URL}/api/auth/refresh`,
        {},
        { withCredentials: true, headers },
      );
      useAuthStore.getState().setSession(response.data);
      return response.data.accessToken;
    } catch {
      useAuthStore.getState().clear();
      return null;
    } finally {
      refreshInflight = null;
    }
  })();

  return refreshInflight;
}

/**
 * Boot-time session restore: after a hard reload the in-memory access
 * token is gone but the wheels_refresh HttpOnly cookie may still be
 * valid. ProtectedRoute awaits this before redirecting to /login.
 */
export function bootstrapSession(): Promise<string | null> {
  return refreshAccessToken(useAuthStore.getState().tenantSlug);
}

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const original = error.config as
      | (InternalAxiosRequestConfig & { _retried?: boolean })
      | undefined;

    // Don't try to refresh on /api/auth/* itself — that loops.
    const url = original?.url ?? '';
    const isAuthEndpoint = url.includes('/api/auth/');

    if (error.response?.status === 401 && original && !original._retried && !isAuthEndpoint) {
      original._retried = true;
      const tenant = useAuthStore.getState().tenantSlug;
      const newToken = await refreshAccessToken(tenant);
      if (newToken) {
        original.headers.Authorization = `Bearer ${newToken}`;
        return apiClient.request(original as AxiosRequestConfig);
      }
    }

    return Promise.reject(error);
  },
);
